import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Container,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useProfile } from "../../../contexts/ProfileContext";
import Cookies from "js-cookie";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3500";

const ProfileReviewStatus = () => {
  const navigate = useNavigate();
  const {
    saveCompleteProfile,
    tempProfileData,
    profileReviewStatus,
    isApprovedAndActive,
    getMergedProfile,
    syncTempProfileDataFromStorage,
    hasPendingUpdateVersion,
    isEditingProfile,
    hasProfileChanges,
    setHasProfileChanges,
  } = useProfile();
  const [reviewStatus, setReviewStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [pendingUpdate, setPendingUpdate] = useState(false);

  const token = Cookies.get("token");

  console.log("Changed Profile Data:", hasProfileChanges);

  useEffect(() => {
    if (profileReviewStatus && profileReviewStatus.review_status) {
      setReviewStatus(profileReviewStatus.review_status);
      setLoading(false);
      return;
    }
    // fallback fetch if context not ready
    const url = `${API_BASE}/api/profile/review-status`;
    fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        setReviewStatus(data.review_status);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [profileReviewStatus]);

  const refreshPendingStatus = useCallback(async () => {
    if (!token) return;
    const pending = await hasPendingUpdateVersion();
    setPendingUpdate(pending);
  }, [token, hasPendingUpdateVersion]);

  useEffect(() => {
    refreshPendingStatus();
  }, [refreshPendingStatus]);

  // Sync tempProfileData from localStorage when component mounts
  useEffect(() => {
    console.log(
      "🔍 DEBUG: ProfileReviewStatus - Component mounted, syncing tempProfileData",
    );
    const syncedData = syncTempProfileDataFromStorage();
    console.log(
      "🔍 DEBUG: ProfileReviewStatus - Synced data result:",
      syncedData,
    );
  }, [syncTempProfileDataFromStorage]);

  // Validation function to check if all required data is entered
  const validateProfileData = () => {
    const errors = [];

    // Get merged profile data (temp + backend)
    const mergedProfile = getMergedProfile();

    console.log("🔍 DEBUG: Validation started");
    console.log("🔍 DEBUG: mergedProfile:", mergedProfile);
    console.log("🔍 DEBUG: tempProfileData:", tempProfileData);
    console.log("🔍 DEBUG: Validation - tempProfileData details:", {
      personalInfo: tempProfileData.personalInfo,
      careerDetails: tempProfileData.careerDetails,
      preferredRoles: tempProfileData.preferredRoles,
      roleExpertise: tempProfileData.roleExpertise,
    });

    // If no profile exists yet, check temp data
    if (!mergedProfile) {
      console.log("🔍 DEBUG: No merged profile found, checking temp data");
      // Check if we have any temp data
      if (
        !tempProfileData.personalInfo &&
        !tempProfileData.careerDetails &&
        !tempProfileData.preferredRoles
      ) {
        console.log("🔍 DEBUG: No temp data found either");
        errors.push(
          "Please complete your profile information before submitting",
        );
        return errors;
      }
    }

    // 1. Validate Personal Info
    const personalInfo =
      mergedProfile?.personal_info || tempProfileData.personalInfo;
    console.log("🔍 DEBUG: personalInfo:", personalInfo);
    if (!personalInfo) {
      console.log("🔍 DEBUG: No personal info found");
      errors.push("Please complete your personal information");
    } else {
      console.log("🔍 DEBUG: Personal info validation - checking fields...");
      console.log("🔍 DEBUG: Checking personal info fields...");
      console.log(
        "🔍 DEBUG: firstName:",
        personalInfo.firstName,
        "first_name:",
        personalInfo.first_name,
      );
      console.log(
        "🔍 DEBUG: lastName:",
        personalInfo.lastName,
        "last_name:",
        personalInfo.last_name,
      );
      console.log("🔍 DEBUG: email:", personalInfo.email);
      console.log(
        "🔍 DEBUG: isAge18OrAbove:",
        personalInfo.isAge18OrAbove,
        "is_age_18_or_above:",
        personalInfo.is_age_18_or_above,
      );
      console.log(
        "🔍 DEBUG: country:",
        personalInfo.country,
        "country_code:",
        personalInfo.country_code,
      );
      console.log("🔍 DEBUG: city:", personalInfo.city);
      console.log(
        "🔍 DEBUG: jobSearchStatus:",
        personalInfo.jobSearchStatus,
        "job_search_status:",
        personalInfo.job_search_status,
      );
      console.log(
        "🔍 DEBUG: function:",
        personalInfo.function,
        "current_industry:",
        personalInfo.current_industry,
      );

      if (!personalInfo.firstName?.trim() && !personalInfo.first_name?.trim()) {
        console.log("🔍 DEBUG: First name missing");
        errors.push("First name is required");
      }
      if (!personalInfo.lastName?.trim() && !personalInfo.last_name?.trim()) {
        console.log("🔍 DEBUG: Last name missing");
        errors.push("Last name is required");
      }
      if (!personalInfo.email?.trim()) {
        console.log("🔍 DEBUG: Email missing");
        errors.push("Email is required");
      }
      if (!personalInfo.isAge18OrAbove && !personalInfo.is_age_18_or_above) {
        console.log("🔍 DEBUG: Age confirmation missing");
        errors.push("You must be 18 or above to continue");
      }
      if (!personalInfo.country?.trim() && !personalInfo.country_code?.trim()) {
        console.log("🔍 DEBUG: Country missing");
        errors.push("Country is required");
      }
      // City is optional, so no validation needed
      // if (!personalInfo.city?.trim()) {
      //   console.log('🔍 DEBUG: City missing');
      //   errors.push('City is required');
      // }
      if (!personalInfo.jobSearchStatus && !personalInfo.job_search_status) {
        console.log("🔍 DEBUG: Job search status missing");
        errors.push("Job search status is required");
      }
      if (!personalInfo.function && !personalInfo.current_industry) {
        console.log("🔍 DEBUG: Function/industry missing");
        errors.push("Current industry/function is required");
      }
    }

    // 2. Validate Career Details
    const careerDetails =
      mergedProfile?.career_details || tempProfileData.careerDetails;
    console.log("🔍 DEBUG: careerDetails:", careerDetails);
    if (!careerDetails) {
      console.log("🔍 DEBUG: No career details found");
      errors.push("Please complete your career details");
    } else {
      console.log(
        "🔍 DEBUG: Career details validation - checking CV/LinkedIn...",
      );
      const hasCV =
        careerDetails.cv_filename ||
        tempProfileData.careerDetails?.selectedFile;
      const hasLinkedIn = careerDetails.linkedin_url?.trim();
      console.log("🔍 DEBUG: hasCV:", hasCV, "hasLinkedIn:", hasLinkedIn);
      console.log("🔍 DEBUG: cv_filename:", careerDetails.cv_filename);
      console.log(
        "🔍 DEBUG: selectedFile:",
        tempProfileData.careerDetails?.selectedFile,
      );
      console.log("🔍 DEBUG: linkedin_url:", careerDetails.linkedin_url);

      if (!hasCV && !hasLinkedIn) {
        console.log("🔍 DEBUG: Neither CV nor LinkedIn provided");
        errors.push("Please either upload a CV or provide your LinkedIn URL");
      }
    }

    // 3. Validate Preferred Roles and Expertise
    const preferredRoles =
      mergedProfile?.preferred_roles || tempProfileData.preferredRoles;
    console.log("🔍 DEBUG: preferredRoles:", preferredRoles);
    if (!preferredRoles || preferredRoles.length === 0) {
      console.log("🔍 DEBUG: No preferred roles found");
      errors.push("Please add at least one preferred role");
    } else {
      console.log(
        "🔍 DEBUG: Preferred roles validation - checking roles and expertise...",
      );
      let hasValidRole = false;

      for (const role of preferredRoles) {
        console.log("🔍 DEBUG: Checking role:", role);
        console.log(
          "🔍 DEBUG: role.function:",
          role.function,
          "role.role:",
          role.role,
        );
        if (role.function && role.role) {
          hasValidRole = true;
          console.log("🔍 DEBUG: Valid role found:", role.function, role.role);

          // Check if expertise questions are answered for this role
          // Check multiple sources for role expertise data
          const tempRoleExpertise = JSON.parse(
            localStorage.getItem("tempRoleExpertise") || "[]",
          );
          const roleExpertise =
            mergedProfile?.role_expertise ||
            tempProfileData.roleExpertise ||
            {};

          // First, try to find in tempProfileData.roleExpertise (array format)
          let expertiseData = null;
          if (
            tempProfileData.roleExpertise &&
            Array.isArray(tempProfileData.roleExpertise)
          ) {
            const tempExpertise = tempProfileData.roleExpertise.find(
              (r) =>
                r.job_function_id === role.function &&
                r.job_role_id === role.role,
            );
            if (tempExpertise && tempExpertise.answers) {
              expertiseData = tempExpertise.answers;
            }
          }

          // If not found, try mergedProfile.role_expertise (array format)
          if (
            !expertiseData &&
            mergedProfile?.role_expertise &&
            Array.isArray(mergedProfile.role_expertise)
          ) {
            const mergedExpertise = mergedProfile.role_expertise.find(
              (r) =>
                r.job_function_id === role.function &&
                r.job_role_id === role.role,
            );
            if (mergedExpertise && mergedExpertise.answers) {
              expertiseData = mergedExpertise.answers;
            }
          }

          // If still not found, try tempRoleExpertise from localStorage
          if (!expertiseData && tempRoleExpertise.length > 0) {
            const localStorageExpertise = tempRoleExpertise.find(
              (r) =>
                r.job_function_id === role.function &&
                r.job_role_id === role.role,
            );
            if (localStorageExpertise && localStorageExpertise.answers) {
              expertiseData = localStorageExpertise.answers;
            }
          }

          console.log("🔍 DEBUG: expertiseData found:", expertiseData);
          console.log(
            "🔍 DEBUG: tempProfileData.roleExpertise:",
            tempProfileData.roleExpertise,
          );
          console.log(
            "🔍 DEBUG: mergedProfile.role_expertise:",
            mergedProfile?.role_expertise,
          );
          console.log(
            "🔍 DEBUG: tempRoleExpertise from localStorage:",
            tempRoleExpertise,
          );

          if (!expertiseData || Object.keys(expertiseData).length === 0) {
            // Try to get role name for better error message
            const roleName = role.role_name || role.role || "this role";
            console.log("🔍 DEBUG: No expertise data for role:", roleName);
            errors.push(`Please answer expertise questions for ${roleName}`);
            break;
          }
        }
      }

      if (!hasValidRole) {
        console.log("🔍 DEBUG: No valid role found");
        errors.push(
          "Please select both function and role for at least one preferred role",
        );
      }
    }

    console.log("🔍 DEBUG: Validation errors:", errors);
    return errors;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");
    setSuccess("");

    try {
      // ALWAYS save current data first, regardless of validation
      console.log("🔍 DEBUG: Saving current data before validation");
      console.log("🔍 DEBUG: tempProfileData before save:", tempProfileData);

      // Try to save any available data, even if some sections are null
      try {
        await saveCompleteProfile();
        console.log("🔍 DEBUG: Current data saved successfully");
      } catch (saveError) {
        console.log("🔍 DEBUG: Save failed:", saveError.message);
        throw new Error("Error in creating candidate profile");
      }

      // VALIDATION COMMENTED OUT - Allow submission regardless of data completeness
      // const validationErrors = validateProfileData();

      // if (validationErrors.length > 0) {
      //   setError('Make sure that the profile, career and role details are filled in before you submit');
      //   setSubmitting(false);
      //   return;
      // }

      const shouldTriggerNewReview = !isApprovedAndActive;

      if (shouldTriggerNewReview) {
        const submitResponse = await fetch(
          `${API_BASE}/api/profile/submit-review`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({}),
          },
        );

        if (!submitResponse.ok) {
          const errorText = await submitResponse.text();
          throw new Error(errorText || "Failed to submit profile for review");
        }
      }

      // Re-fetch status so UI stays in sync with backend
      try {
        const res = await fetch(`${API_BASE}/api/profile/review-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setReviewStatus(data.review_status);
        }
      } catch (statusError) {
        console.log("🔍 DEBUG: Unable to refresh review status", statusError);
      }

      await refreshPendingStatus();

      setSuccess("Successfully submitted for review");
      setTimeout(() => setSuccess(""), 5000);

      // Reset the profile changes flag after successful submission
      setHasProfileChanges(false);
    } catch (error) {
      console.error("Error submitting profile:", error);
      setError(error.message || "Failed to submit profile for review");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <CircularProgress sx={{ mt: 8 }} />;

  const latestVersionStatus = profileReviewStatus?.latest_version_status;
  const latestVersionComment = profileReviewStatus?.latest_version_comment;
  const latestVersionUpdatedAt = profileReviewStatus?.latest_version_updated_at;

  const isLatestRejected =
    typeof latestVersionStatus === "string" &&
    latestVersionStatus.toLowerCase() === "rejected";

  const formattedRejectionTime = (() => {
    if (!latestVersionUpdatedAt) return null;
    const date = new Date(latestVersionUpdatedAt);
    return isNaN(date.getTime()) ? null : date.toLocaleString();
  })();
  return (
    <Paper elevation={3} sx={{ p: 4, width: "100%", mx: "auto", mt: 0 }}>
      <Container maxWidth="md">
        {isLatestRejected && (
          <Alert severity="error" sx={{ mb: 3, textAlign: "left" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Your last update was rejected.
            </Typography>
            <Typography
              variant="body2"
              sx={{ mb: latestVersionComment ? 1 : 0 }}
            >
              Please review the notes below and make the necessary changes
              before resubmitting.
            </Typography>
            {latestVersionComment && (
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  p: 1.5,
                  borderRadius: 1,
                  backgroundColor: "rgba(244, 67, 54, 0.08)",
                  border: "1px solid rgba(244, 67, 54, 0.2)",
                  whiteSpace: "pre-wrap",
                }}
              >
                {latestVersionComment}
              </Typography>
            )}
            {formattedRejectionTime && (
              <Typography
                variant="caption"
                sx={{ display: "block", mt: 1, color: "text.secondary" }}
              >
                Updated on {formattedRejectionTime}
              </Typography>
            )}
          </Alert>
        )}
        <Box sx={{ textAlign: "center", mb: 6, mt: 4 }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 500,
              mb: 2,
              color:
                isApprovedAndActive && !pendingUpdate ? "#9b42e9" : "inherit",
            }}
          >
            {isApprovedAndActive
              ? pendingUpdate
                ? "Profile update under review"
                : "Your profile is approved and live!"
              : isLatestRejected
                ? "Profile changes needed"
                : reviewStatus === "Under Review"
                  ? "Profile submitted for review"
                  : "Expert profile review & feedback"}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4 }}>
            {isApprovedAndActive
              ? pendingUpdate
                ? "Your updates are being reviewed. Your approved profile remains active. You cannot submit additional changes until the current request is processed."
                : "You can update your profile anytime. Submit any changes for a quick review so we can keep your profile current."
              : isLatestRejected
                ? "Please review the feedback above and resubmit your profile."
                : reviewStatus === "Under Review"
                  ? "Our experts are reviewing your profile. Once approved, you'll be invited to a 1:1 session with EQuAlly to create your Career Journey."
                  : "Submit your profile for expert review. Once approved, you'll be invited to a 1:1 session with EQuAlly to create your Career Journey."}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Divider sx={{ my: 3 }} />

          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={
              submitting ||
              pendingUpdate ||
              (!isApprovedAndActive && reviewStatus === "Under Review") ||
              (!isEditingProfile && !hasProfileChanges) ||
              (isEditingProfile && !hasProfileChanges)
            }
            sx={{ mt: 2, mb: 2 }}
          >
            {submitting
              ? "Saving Profile..."
              : pendingUpdate
                ? "Profile Update Under Review"
                : reviewStatus === "Under Review"
                  ? "Submitted for Review"
                  : isApprovedAndActive
                    ? "Submit Updates for Review"
                    : "Submit for Review"}
          </Button>
        </Box>
      </Container>
    </Paper>
  );
};

export default ProfileReviewStatus;
